import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import qs from 'qs';

import { charactersData, charactersTagsData } from '@data/characters';

import { fetchMessagesCount } from '@api/fetchers/messagesCount';

import { Container } from '@ui/Container';
import { Select } from '@ui/Select';
import { QueryPagination } from '@ui/QueryPagination';

import Header from '@components/layout/Header';
import AiGirlCard from '@components/elements/card/AiGirlCard';
import SEOHead from '@components/SEOHead';
import { Footer } from '@components/layout/Footer';
import { QuickFilters } from '@components/AiGirlsPage/QuickFilters';
import { CatalogBanner } from '@components/AiGirlsPage/CatalogBanner';

import { decodeMessageCount } from '@utils/messageCount';

import styles from '../styles/EmailCollector.module.css';
import { DualAppBanner } from '@components/SweetWaifu/DualAppBanner';

const SortingOptions = {
  Default: null,
  Popularity: 'popularity',
  Newest: 'newest',
  Oldest: 'oldest',
};

const CHARACTERS_PER_PAGE = 30;

function useSortByOptions() {
  const { t: tt } = useTranslation('characters_catalog');

  return useMemo(
    () => [
      {
        value: SortingOptions.Default,
        label: tt('sort_by.item.default'),
      },
      {
        value: SortingOptions.Popularity,
        label: tt('sort_by.item.popularity'),
      },
      {
        value: 'newest',
        label: tt('sort_by.item.newest'),
      },
      // {
      //   value: 'oldest',
      //   label: tt('sort_by.item.oldest'),
      // },
    ],
    [tt],
  );
}

const AiGirls = ({
  totalPages,
  page,
  tag,
  sort,
  characters,
  charactersTags,
}) => {
  const { t: tDataGirls } = useTranslation('data_characters');
  const { t: tDataGirlsTags } = useTranslation('data_characters_tags');
  const { t: tt } = useTranslation('characters_catalog');

  const router = useRouter();

  const sortByOptions = useSortByOptions();

  return (
    <div>
      <SEOHead
        title={tt('seo.title')}
        description={tt('seo.description')}
        type="website"
        keywords={tt('seo.keywords')}
        url="https://www.aigirlfriend.wtf/ai-girls"
        imageUrl="https://www.aigirlfriend.wtf/web-banner-min.jpg"
      />

      <Header />

      <Container as="main" className="pt-[80px]">
        <CatalogBanner />

        <div className="flex flex-col lg:flex-row justify-between items-start gap-[24px] mb-[48px] mt-[24px]">
          <QuickFilters
            items={charactersTags}
            value={tag}
            onChange={(v) => {
              const query = { ...router.query };
              if (!v) {
                delete query.tag;
              } else {
                query.tag = v;
              }
              query.page = String(1);

              return router.push({ query });
            }}
          />

          <Select
            label={tt('sort_by.label')}
            placeholder={tt('sort_by.placeholder')}
            options={sortByOptions}
            value={sort}
            onChange={(v) => {
              const query = { ...router.query, page: String(1) };
              if (!v) {
                delete query.sort;
              } else {
                query.sort = v;
              }

              return router.push({ query });
            }}
            className="shrink-0"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 mb-[48px]">
          {characters.map((character) => (
            <AiGirlCard
              key={character.name}
              girlName={tDataGirls(character.name)}
              img={character.img}
              description={tDataGirls(character.description)}
              linkToChat={character.linkToChat}
              msgCounterValue={character.messageCount}
              tags={character.tags.map(({ id, label }) => ({
                id,
                label: tDataGirlsTags(label),
              }))}
            />
          ))}
        </div>

        <div className="flex flex-row justify-center">
          <QueryPagination
            queryParamName="page"
            pagesCount={totalPages}
            pagesRange={3}
            page={page}
            prevButtonAccessibleLabel={tt(
              'pagination.prevPageButtonAccessibleLabel',
            )}
            nextButtonAccessibleLabel={tt(
              'pagination.nextPageButtonAccessibleLabel',
            )}
          />
        </div>

        <div className="flex flex-row justify-center mt-[64px]">
          <div className={'flex flex-col gap-4 items-center'}>
            <DualAppBanner />
            {/*<span>{tt('did_not_find.text')}</span>*/}
            {/*<Link*/}
            {/*  className={styles.submitButton}*/}
            {/*  href={'/create-your-ai-girlfriend'}*/}
            {/*>*/}
            {/*  {tt('did_not_find.action')}*/}
            {/*</Link>*/}
          </div>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

/**
 * @param {string | string[] | undefined} queryParam
 * @param {(v: string | string[]) => any} [transformFn]
 *
 * @returns {any}
 */
function parseQueryParam(queryParam, transformFn) {
  if (typeof queryParam === 'string') {
    if (!queryParam) {
      return null;
    }

    return transformFn ? transformFn(queryParam) : queryParam;
  }

  if (Array.isArray(queryParam)) {
    let val = queryParam[0];

    if (!val) {
      return null;
    }

    return transformFn ? transformFn(val) : val;
  }

  return null;
}

/**
 * @param params
 * @param {string} params.locale
 * @param {number} params.page
 * @param {string | null} params.tag
 * @param {string | null} params.sort
 *
 * @returns string
 */
function getRedirectDestination({ locale, page, tag, sort }) {
  const localePart = locale === 'en' ? '' : `${locale}/`;

  let destination = `/${localePart}ai-girls`;

  const query = qs.stringify({
    page,
    tag: tag ?? undefined,
    sort: sort ?? undefined,
  });
  if (query) {
    destination += `?${query}`;
  }

  return destination;
}

export async function getServerSideProps({ locale, query, res }) {
  // Set cache headers -> cache results for 30 minutes
  res.setHeader(
    'Cache-Control',
    'public, max-age=1800, stale-while-revalidate=3600',
  );

  // Get actual query values
  const page = parseQueryParam(query.page, (v) => parseInt(v, 10));
  const tag = parseQueryParam(query.tag);
  const sort = parseQueryParam(query.sort);

  // Get message counts -> required as a part of `filtering` and `sorting`
  let messagesCounts = null;
  try {
    const messagesCountsResponse = await fetchMessagesCount();
    messagesCounts = await messagesCountsResponse.json();
  } catch (e) {
    console.error('Error fetching messages counts:', e);
  }

  let characters = charactersData;

  // Apply relevant message count to each character
  characters = characters.map((character) => {
    const dynamicData =
      messagesCounts &&
      messagesCounts.find((item) => item.ai_girl === character.counterName);

    return {
      ...character,
      messageCount: dynamicData ? dynamicData.message_count : 'NEW',
    };
  });

  // Apply filtering -> only if `tag` is present
  if (tag) {
    switch (tag) {
      case 'NEW':
        characters = characters.filter(
          (character) => character.messageCount === 'NEW',
        );
        break;
      default:
        characters = characters.filter((character) =>
          character.tags.some((t) => t.id === tag),
        );
        break;
    }
  }

  // Apply sorting -> only if `sort` is present
  switch (sort) {
    case SortingOptions.Popularity:
      characters.sort(
        (a, b) =>
          decodeMessageCount(b.messageCount) -
          decodeMessageCount(a.messageCount),
      );
      break;
    case SortingOptions.Newest:
      characters.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
      break;
    default:
      break;
  }

  // Apply pagination
  const totalPages = Math.ceil(characters.length / CHARACTERS_PER_PAGE);

  const isPageValidNumber = typeof page === 'number' && !Number.isNaN(page);

  /*
    If passed page param is invalid -> redirect to the first/last page.

    Page is considered invalid if it's a number, exceeding [1...totalPages] interval.
    If the page is not a number, just skip this.

    This redirect is temporary and should not be cached by robots, since should not happen in common use cases.
  */
  if (isPageValidNumber && (page < 1 || page > totalPages)) {
    let targetPage = 1;
    if (page > totalPages) {
      targetPage = totalPages;
    }

    return {
      redirect: {
        destination: getRedirectDestination({
          locale,
          page: targetPage,
          tag: tag,
          sort: sort,
        }),
        permanent: false,
      },
    };
  }

  let pageToUse = isPageValidNumber ? page : 1;
  characters = characters.slice(
    (pageToUse - 1) * CHARACTERS_PER_PAGE,
    pageToUse * CHARACTERS_PER_PAGE,
  );

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'header',
        'data_characters',
        'data_characters_tags',
        'characters_catalog',
        'footer',
      ])),
      totalPages,
      page: pageToUse,
      tag: tag,
      sort: sort,
      characters,
      charactersTags: charactersTagsData,
    },
  };
}

export default AiGirls;
